import type { SequenceToken } from "../types/action";

export interface NoteState {
  readonly guesses: Readonly<Record<string, string | null>>;
}

export function createEmptyNoteState(): NoteState {
  return { guesses: {} };
}

export function setNoteGuess(
  state: NoteState,
  symbol: string,
  guess: string | null,
): NoteState {
  return {
    guesses: {
      ...state.guesses,
      [symbol]: guess,
    },
  };
}

function isRepeatToken(token: SequenceToken): boolean {
  return token === "3" || token === "5";
}

function toNoteKey(token: SequenceToken): string {
  if (token === "3") return "③";
  if (token === "5") return "⑤";
  return token;
}

export function buildGuessDisplay(
  state: NoteState,
  rawTokens: readonly SequenceToken[],
): string {
  const seen = new Set<string>();
  const parts: string[] = [];

  for (const token of rawTokens) {
    if (isRepeatToken(token)) {
      continue;
    }
    if (seen.has(token)) {
      continue;
    }
    seen.add(token);

    const guess = state.guesses[toNoteKey(token)] ?? null;
    parts.push(`${token}=${guess ?? "?"}`);
  }

  return parts.join(" ");
}

export interface GuessSequenceResult {
  readonly text: string;
  readonly allUnknown: boolean;
}

export function buildGuessSequence(
  state: NoteState,
  rawTokens: readonly SequenceToken[],
): GuessSequenceResult {
  const parts: string[] = [];
  let hasAnyGuess = false;

  for (const token of rawTokens) {
    const guess = state.guesses[toNoteKey(token)];
    if (guess !== undefined && guess !== null) {
      parts.push(guess);
      hasAnyGuess = true;
    } else {
      parts.push("?");
    }
  }

  return { text: parts.join(""), allUnknown: !hasAnyGuess };
}
